import { useState } from "react";
import { Link } from "react-router-dom";
import { ShieldCheck, AlertTriangle } from "lucide-react";
import { useFetch, Panel, Stat, Tag, PageHeader, Empty, pnlClass } from "@/components/Common";
import { fmtMoney, fmtTime, fmtDur } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const ANY = "any";

function EventCard({ e }) {
  const a = e.analysis;
  return (
    <div className="rounded border bg-card/70 p-3" data-testid={`intel-event-${e.id}`}>
      <div className="flex flex-wrap items-center gap-1.5">
        <Tag tone={e.official ? "gold" : "muted"}>{e.official ? "official" : "media"}</Tag><Tag>{e.country}</Tag><Tag>{e.language}</Tag>
        {e.official_confirmed && !e.official && <Tag tone="up">official confirmation</Tag>}
        {e.rumor_flag && <Tag tone="warn">rumor / unverified</Tag>}
        {a?.high_impact && <Tag tone="down">high impact</Tag>}
        {e.cluster_size > 1 && <Tag tone="blue">{e.cluster_size} sources</Tag>}
        {e.revisions?.length > 0 && <Tag tone="warn">revised ×{e.revisions.length}</Tag>}
        {e.categories?.slice(0, 3).map((c) => <Tag key={c}>{c.replace(/_/g, " ")}</Tag>)}
      </div>
      <a href={e.url} target="_blank" rel="noreferrer" className="mt-2 block text-sm font-medium hover:text-primary">{e.title_en || e.title}</a>
      {e.title_en && e.title_en !== e.title && <div className="text-xs text-muted-foreground">Original: {e.title}</div>}
      <div className="mt-1 font-mono text-[10px] text-muted-foreground">
        {e.source_name} · published {fmtTime(e.published_at)} · first detected {fmtTime(e.first_detected_at)}{e.detection_latency_s != null && ` · latency ${fmtDur(e.detection_latency_s)}`}
      </div>
      {a && (
        <div className="mt-2 space-y-1 border-l-2 border-primary/40 pl-3 text-xs">
          <div><span className="label">Fact </span>{a.facts}</div>
          <div className="text-muted-foreground"><span className="label">AI interpretation </span>{a.interpretation}{a.speculation && " (speculative)"}</div>
          <div className="flex flex-wrap gap-1">{(a.impacts || []).map((i, k) => <Tag key={k} tone={i.direction === "up" ? "up" : i.direction === "down" ? "down" : "muted"}>{i.symbol} {i.direction} · {i.confidence}</Tag>)}</div>
        </div>
      )}
    </div>
  );
}

function Influence() {
  const { data } = useFetch("/intel/influence", [], 60000);
  return (
    <Panel title="Events → trade decisions → outcome" testid="intel-influence-panel">
      {data?.length ? data.map((r) => (
        <div key={r.id} className="border-b border-border/50 py-2 text-sm">
          <div className="flex flex-wrap items-center gap-2"><span className="font-mono">{r.symbol}</span><Tag>{r.direction}</Tag><Tag>{r.strategy}</Tag><Tag tone={r.action === "executed" ? "up" : "warn"}>{r.stage}: {r.action}</Tag>
            {r.trade && <span className={`num ${pnlClass(r.trade.pnl)}`}>{r.outcome ? `${r.outcome} ${fmtMoney(r.trade.pnl)}` : "open"}</span>}<span className="font-mono text-[10px] text-muted-foreground">{fmtTime(r.created_at)}</span></div>
          <ul className="mt-1 list-disc pl-5 text-xs text-muted-foreground">{(r.news?.intel || []).map((n) => <li key={n.id}>{n.official ? "[official] " : ""}{n.title}</li>)}</ul>
        </div>
      )) : <Empty>No trade decision has been evaluated alongside related intelligence events yet. News context never triggers a trade on its own.</Empty>}
    </Panel>
  );
}

export default function Intelligence() {
  const [f, setF] = useState({ country: ANY, language: ANY, market: ANY, official: ANY, q: "" });
  const qs = Object.entries(f).filter(([, v]) => v && v !== ANY).map(([k, v]) => `${k}=${encodeURIComponent(v)}`).join("&");
  const { data: events } = useFetch(`/intel/events?limit=120${qs ? "&" + qs : ""}`, [], 30000);
  const { data: s } = useFetch("/intel/summary", [], 30000);
  const sel = (k, opts, ph) => (
    <Select value={f[k]} onValueChange={(v) => setF({ ...f, [k]: v })}>
      <SelectTrigger className="w-36" data-testid={`intel-filter-${k}`}><SelectValue placeholder={ph} /></SelectTrigger>
      <SelectContent><SelectItem value={ANY}>All {ph}</SelectItem>{opts.map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}</SelectContent>
    </Select>
  );
  return (
    <div className="space-y-4">
      <PageHeader title="Global Intelligence" sub="Official releases, open data APIs, GDELT and multilingual publisher feeds — timestamped, deduplicated, translated and mapped to instruments" right={<Link to="/sources" className="text-sm text-primary hover:underline" data-testid="intel-sources-link">Source manager →</Link>} />
      {s && (
        <div className="grid gap-3 sm:grid-cols-3 xl:grid-cols-6">
          <Stat label="Sources connected" value={`${s.sources_connected}/${s.sources_total}`} testid="intel-sources-connected" />
          <Stat label="Events (24h)" value={s.events_24h} sub={`${s.events_total} total`} testid="intel-events-24h" />
          <Stat label="Languages" value={s.languages.length} sub={s.languages.join(" ")} />
          <Stat label="Median detection latency" value={fmtDur(s.latency_24h.median_s)} sub={`p90 ${fmtDur(s.latency_24h.p90_s)} · n=${s.latency_24h.n} live items`} />
          <Stat label="Feed health" value={s.intel_feed_ok ? "OK" : "OUTAGE"} tone={s.intel_feed_ok ? "text-up" : "text-down"} sub="engine blocks entries on outage" testid="intel-feed-health" />
          <Stat label="AI cost (est.)" value={`$${s.ai_usage.reduce((a, u) => a + u.est_cost_usd, 0).toFixed(3)}`} sub={s.ai_usage.map((u) => `${u.kind} ${u.calls}`).join(" · ")} />
        </div>
      )}
      <div className="grid gap-4 xl:grid-cols-[1.6fr_1fr]">
        <Panel title="Live world feed" testid="intel-feed" right={
          <div className="flex flex-wrap gap-2">
            {sel("country", s?.countries || [], "countries")}{sel("language", s?.languages || [], "languages")}{sel("market", ["forex", "gold", "stocks"], "markets")}
            <Select value={f.official} onValueChange={(v) => setF({ ...f, official: v })}><SelectTrigger className="w-32" data-testid="intel-filter-official"><SelectValue /></SelectTrigger><SelectContent><SelectItem value={ANY}>All sources</SelectItem><SelectItem value="true">Official only</SelectItem><SelectItem value="false">Media only</SelectItem></SelectContent></Select>
            <Input placeholder="Search" value={f.q} onChange={(e) => setF({ ...f, q: e.target.value })} className="w-36" data-testid="intel-search-input" />
          </div>}>
          <div className="max-h-[900px] space-y-2 overflow-auto">{events?.length ? events.map((e) => <EventCard key={e.id} e={e} />) : <Empty>No events match</Empty>}</div>
        </Panel>
        <div className="space-y-4">
          <Panel title="High-impact alerts (AI-flagged, 24h)" testid="intel-alerts" right={<AlertTriangle size={16} className="text-down" />}>
            {s?.alerts?.length ? <div className="space-y-2">{s.alerts.map((e) => <EventCard key={e.id} e={e} />)}</div> : <Empty>None flagged</Empty>}
          </Panel>
          <Influence />
          <Panel title="Coverage gaps" right={<ShieldCheck size={16} className="text-gold" />}>
            <div className="flex flex-wrap gap-1">{s?.coverage_gaps?.map((c) => <Tag key={c} tone="warn">{c}</Tag>)}</div>
            <p className="mt-2 text-xs text-muted-foreground">{s?.latency_24h.note}</p>
            <p className="mt-1 text-xs text-muted-foreground">{s?.ai_cost_note}</p>
          </Panel>
        </div>
      </div>
    </div>
  );
}
